import { useCallback, useMemo } from "react";
import { useQuery } from "@apollo/client";

import { GET_OPPORTUNITTIES_TYPES } from "../graphql/query";

// Iterative BFS — flat opportunity-type lists are tiny in practice
// (dealership configs), but using BFS over a Map keeps this O(n) and
// avoids any recursion / stack risk.
const buildChildrenByParentMap = typesList => {
  const childrenByParent = new Map();
  (typesList || []).forEach(t => {
    const pid = t?.parentType?.id;
    if (pid == null) return;
    const key = +pid;
    if (!childrenByParent.has(key)) childrenByParent.set(key, []);
    childrenByParent.get(key).push(+t.id);
  });
  return childrenByParent;
};

// Expand a parent type id into [parent + every descendant]. The
// dashboard drill-in destinations (Lead Center / Activity Center /
// detail reports) filter by exact leaf type ids on the lead row, so a
// bare parent id alone returns zero rows.
const expandWithMap = (rootId, childrenByParent) => {
  if (rootId == null) return [];
  const root = +rootId;
  const out = [root];
  const queue = [root];
  while (queue.length) {
    const next = queue.shift();
    (childrenByParent.get(next) || []).forEach(childId => {
      out.push(childId);
      queue.push(childId);
    });
  }
  return out;
};

/**
 * Builds a parent-child tree from a flat list of opportunity types.
 * Uses parentType.id to establish relationships.
 *
 * @param {Array} flatList - Raw opportunity types from API
 * @param {boolean} includeIndustry - Whether to wrap tree under industry nodes
 * @returns {Array} Tree structured for TreeComponent ({ name, key, children })
 */
const buildTree = (flatList, includeIndustry = false) => {
  if (!flatList || flatList.length === 0) return [];

  const industriesMap = new Map();
  const itemsMap = new Map();

  // First pass: index all items and auto-create missing parents
  flatList.forEach(item => {
    const itemId = String(item.id);
    const industryId = String(item.industry?.id || "other");
    const industryName = item.industry?.name || "Other";

    if (includeIndustry && !industriesMap.has(industryId)) {
      industriesMap.set(industryId, {
        name: industryName,
        key: `industry-${industryId}`,
        children: [],
      });
    }

    if (!itemsMap.has(itemId)) {
      itemsMap.set(itemId, {
        name: item.name || "Unknown",
        key: +item.id,
        children: [],
        parentTypeId: item.parentType?.id ? String(item.parentType.id) : null,
        industryId,
      });
    } else {
      const existing = itemsMap.get(itemId);
      existing.name = item.name || "Unknown";
      existing.key = +item.id;
      existing.parentTypeId = item.parentType?.id ? String(item.parentType.id) : null;
      existing.industryId = industryId;
    }

    // Auto-create parent node if referenced but not in list
    if (item.parentType?.id) {
      const parentId = String(item.parentType.id);
      if (!itemsMap.has(parentId)) {
        itemsMap.set(parentId, {
          name: item.parentType.name || "Unknown",
          key: +item.parentType.id,
          children: [],
          parentTypeId: null,
          industryId,
        });
      }
    }
  });

  // Second pass: build parent-child relationships
  const roots = [];

  itemsMap.forEach(currentItem => {
    if (currentItem.parentTypeId) {
      const parent = itemsMap.get(currentItem.parentTypeId);
      if (parent) {
        parent.children.push(currentItem);
      } else if (includeIndustry) {
        const industry = industriesMap.get(currentItem.industryId);
        industry?.children.push(currentItem);
      } else {
        roots.push(currentItem);
      }
    } else if (includeIndustry) {
      const industry = industriesMap.get(currentItem.industryId);
      industry?.children.push(currentItem);
    } else {
      roots.push(currentItem);
    }
  });

  // Clean up temp props
  const clean = node => {
    const cleaned = { name: node.name, key: node.key };
    if (node.children && node.children.length > 0) {
      cleaned.children = node.children.map(clean);
    }
    return cleaned;
  };

  if (includeIndustry) {
    return Array.from(industriesMap.values()).map(clean);
  }

  return roots.map(clean);
};

/**
 * Hook to fetch opportunity types and return a parent-child tree.
 * Tree is formatted for TreeComponent: { name, key, children }
 *
 * @param {Object} options
 * @param {number} options.industryId - Industry ID for the query (default: 1)
 * @param {boolean} options.includeIndustry - Wrap tree under industry nodes (default: false)
 * @param {boolean} options.skip - Skip query execution
 * @returns {{
 *   loading: boolean,
 *   treeData: Array,
 *   opportunityTypes: Array,
 *   expandWithDescendants: (rootId:number|string) => number[],
 * }}
 */
const useOpportunityTypeTree = (options = {}) => {
  const { industryId = 1, includeIndustry = false, skip = false } = options;

  const { data, loading } = useQuery(GET_OPPORTUNITTIES_TYPES, {
    variables: { industryId },
    fetchPolicy: "cache-first",
    skip,
  });

  const opportunityTypes = data?.getOpportunityTypes || [];

  const treeData = useMemo(() => {
    return buildTree(opportunityTypes, includeIndustry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, includeIndustry]);

  // Map is rebuilt only when the underlying data changes — callers can
  // invoke `expandWithDescendants` repeatedly without re-walking the list.
  const childrenByParent = useMemo(
    () => buildChildrenByParentMap(opportunityTypes),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  const expandWithDescendants = useCallback(
    rootId => expandWithMap(rootId, childrenByParent),
    [childrenByParent]
  );

  return { loading, treeData, opportunityTypes, expandWithDescendants };
};

export default useOpportunityTypeTree;
